import { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Leaf, Brain, Shield, Check, Sparkles, User, Heart, ChevronRight, ChevronDown, Zap, Lock, Crown, ShoppingBag, BookOpen, Store, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import { SocialLinks, InstagramIcon, TikTokIcon, YouTubeIcon } from "../components/Header";
import { Testimonials } from "../components/Testimonials";
import { HerbOfWeek } from "../components/HerbOfWeek";

const TRUST_BADGES = [
  { icon: Check, label: "100% free — no account needed" },
  { icon: Brain, label: "AI-personalised to your answers" },
  { icon: Shield, label: "Considers medications & safety" },
];

const YOU_GET = [
  "5 personalised herb suggestions",
  "Visual body map",
  "Herbal recipes",
  "Safety warnings",
  "One-click shop basket",
  "Downloadable plan",
];

const WHY_CHOOSE = [
  "Personalised recommendations",
  "AI-powered analysis",
  "Educational guidance",
  "Designed around your goals",
  "Quick and easy to use",
];

const SampleResultCard = ({ className = "" }) => (
  <div className={`bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 p-4 text-left ${className}`} data-testid="home-sample-result">
    <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-2">Sample result · what you'll receive</p>
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-primary/25">
        <Leaf className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="font-bold text-gray-900 leading-tight">Ashwagandha <span className="text-xs text-gray-400 font-normal italic">Withania somnifera</span></p>
        <p className="text-xs text-gray-600 mt-0.5">Traditionally used to support stress balance and restful sleep.</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5 mt-2.5">
      {["Tea or capsule", "Dosage guidance", "Safety notes"].map((c) => (
        <span key={c} className="text-[10px] bg-emerald-50 text-emerald-700 font-semibold rounded-full px-2 py-0.5">{c}</span>
      ))}
    </div>
    <p className="text-[10px] text-gray-400 mt-2.5 border-t border-gray-100 pt-2">
      + 4 more herbs, recipes and a body map — personalised to <b>your</b> answers.
    </p>
  </div>
);

const Home = () => {
  const { user, hasSignedAgreement, isPremium, requirePremium } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 450);
    }
  }, [hash]);

  const gatedPremium = (path) => (e) => {
    e?.preventDefault();
    if (requirePremium(path)) navigate(path);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ============ HERO — single goal: start the free assessment ============ */}
      <section className="relative pt-10 pb-12 lg:py-20 overflow-hidden bg-gradient-to-b from-emerald-50/60 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-5 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-xs sm:text-sm font-semibold text-primary tracking-wide">FREE PERSONALISED HERBAL ASSESSMENT</span>
              </div>

              <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Your Wellness. Your Profile.{" "}
                <span className="text-primary">Your Recommendations.</span>
              </h1>

              <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Answer a few quick questions and receive educational herbal suggestions matched to your
                goals, age, medications and lifestyle — in under 2 minutes.
              </p>

              <div className="flex flex-col items-center lg:items-start gap-3">
                <button
                  onClick={() => navigate("/questionnaire")}
                  data-testid="hero-full-assessment-btn"
                  className="w-full sm:w-auto h-16 px-10 rounded-full bg-primary hover:bg-emerald-800 text-white text-lg font-bold shadow-xl shadow-emerald-600/30 hover:shadow-2xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                >
                  Start Your Free Assessment
                  <ArrowRight className="w-6 h-6" />
                </button>
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
                  <Link
                    to="/store"
                    data-testid="hero-browse-shop-btn"
                    className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm border border-emerald-200 bg-white px-5 py-2.5 rounded-full hover:bg-emerald-50 transition-colors"
                  >
                    <ShoppingBag className="w-4 h-4" /> Browse the Herb Shop
                  </Link>
                  <a
                    href="#how-it-works"
                    data-testid="hero-learn-how-btn"
                    className="inline-flex items-center gap-1.5 text-gray-500 font-semibold text-sm hover:text-primary hover:underline underline-offset-4"
                  >
                    How it works <ChevronDown className="w-4 h-4" />
                  </a>
                  <a href="#vitality-tests" data-testid="hero-vitality-test-btn" className="inline-flex items-center gap-1.5 text-amber-700 font-semibold text-sm hover:underline underline-offset-4">
                    <Zap className="w-4 h-4" /> Premium Vitality Tests
                  </a>
                </div>
                <div className="flex flex-wrap justify-center lg:justify-start gap-2 pt-1" data-testid="trust-badges">
                  {TRUST_BADGES.map(({ icon: Icon, label }) => (
                    <span key={label} className="inline-flex items-center gap-1.5 bg-white border border-emerald-100 shadow-sm rounded-full px-3.5 py-1.5 text-xs text-gray-700 font-medium">
                      <Icon className="w-3.5 h-3.5 text-emerald-700" /> {label}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-400" data-testid="hero-disclaimer">
                  Educational wellness guidance. Not medical diagnosis or treatment.
                </p>
                <p className="text-xs text-gray-400" data-testid="hero-practitioner-referral">
                  Need one-to-one human support? Find a qualified herbalist via the{" "}
                  <a href="https://www.herbalalliance.org/" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline underline-offset-2">
                    Herbal Alliance directory
                  </a>.
                </p>
                <SampleResultCard className="lg:hidden w-full max-w-md mt-2" />
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-200/50">
                <img
                  src="https://images.unsplash.com/photo-1596344084757-b83f2081da8b?w=800&q=85"
                  alt="Beautiful medicinal herbs collection"
                  className="w-full h-auto max-h-[560px] object-cover"
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 max-w-[310px]">
                <SampleResultCard />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ THREE JOURNEYS — individuals / shoppers / B2B ============ */}
      <section className="py-14 lg:py-18 bg-white" data-testid="home-shops-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-2">Where would you like to start?</h2>
            <p className="text-gray-600">Three simple ways to use Elite Herbalist.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="journey-chooser">
            {/* Individuals */}
            <div className="group bg-emerald-50/60 border-2 border-emerald-100 hover:border-primary/40 rounded-3xl p-7 flex flex-col transition-all hover:shadow-xl" data-testid="journey-individual-card">
              <div className="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-1.5">For you</p>
              <h3 className="font-playfair text-xl font-bold text-gray-900 mb-2">Get your personal herb plan</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1">
                Take the free 2-minute assessment and receive educational herb suggestions matched to your goals and safety profile.
              </p>
              <button onClick={() => navigate("/questionnaire")} data-testid="journey-individual-btn"
                      className="w-full h-12 rounded-full bg-primary hover:bg-emerald-800 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors">
                Take the Free Assessment <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Shoppers */}
            <div className="group bg-white border-2 border-gray-100 hover:border-primary/40 rounded-3xl p-7 flex flex-col transition-all hover:shadow-xl" data-testid="journey-shopper-card">
              <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                <ShoppingBag className="w-6 h-6 text-primary" />
              </div>
              <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-1.5">For shoppers</p>
              <h3 className="font-playfair text-xl font-bold text-gray-900 mb-2">Shop 95+ real botanicals</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
                Single herbs, protocol bundles and reviews — typically ships in 3–7 working days. Plus our free herb encyclopedia.
              </p>
              <Link to="/store" data-testid="home-herb-store-card"
                    className="w-full h-12 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors">
                Browse the Herb Store <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="flex justify-center gap-4 mt-3 text-xs font-semibold">
                <Link to="/herbs" className="text-gray-500 hover:text-primary inline-flex items-center gap-1" data-testid="journey-encyclopedia-link"><BookOpen className="w-3.5 h-3.5" /> Encyclopedia</Link>
                <Link to="/shop" className="text-gray-500 hover:text-primary inline-flex items-center gap-1" data-testid="home-infuso-card"><Leaf className="w-3.5 h-3.5" /> Infuso Fruit Teas</Link>
              </div>
            </div>

            {/* B2B */}
            <div className="group bg-[#0F3D2E] rounded-3xl p-7 flex flex-col transition-all hover:shadow-xl" data-testid="home-store-owners-cta">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center mb-4">
                <Store className="w-6 h-6 text-emerald-200" />
              </div>
              <p className="text-[11px] font-bold text-emerald-300 uppercase tracking-widest mb-1.5">For store owners · B2B</p>
              <h3 className="font-playfair text-xl font-bold text-white mb-2">Put our AI on your website</h3>
              <p className="text-sm text-emerald-100/80 leading-relaxed mb-5 flex-1">
                White-label the assessment under your brand with your products. Free pilot — £0 setup, £0 monthly.
              </p>
              <Link to="/white-label" data-testid="home-store-owners-btn"
                    className="w-full h-12 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 font-bold text-sm flex items-center justify-center gap-2 transition-colors">
                Explore the B2B Programme <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how-it-works" className="py-14 lg:py-18 bg-gradient-to-b from-emerald-50/50 to-white scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-2">How Elite Herbalist Works</h2>
            <p className="text-gray-600">Four simple steps from questions to your personal plan.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: 1, icon: User, title: "Tell us about yourself", text: "Your goals, lifestyle and relevant considerations — about 2 minutes." },
              { n: 2, icon: Brain, title: "AI matches your profile", text: "Our system identifies herbal wellness options relevant to your answers." },
              { n: 3, icon: Leaf, title: "Explore your plan", text: "Herb suggestions, a visual body map, recipes and a downloadable plan." },
              { n: 4, icon: Shield, title: "Decide safely", text: "Clear precautions, interaction warnings and professional-referral guidance." },
            ].map(({ n, icon: Icon, title, text }, i) => (
              <div key={n} className="relative">
                <div className="bg-white rounded-3xl p-6 border border-gray-100 h-full shadow-sm hover:shadow-lg hover:border-primary/30 transition-all text-center">
                  <div className="w-12 h-12 mx-auto bg-primary rounded-2xl flex items-center justify-center mb-4 shadow-md shadow-primary/25">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-[11px] font-bold text-primary uppercase tracking-widest mb-1.5">Step {n}</p>
                  <h3 className="font-playfair text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{text}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6 text-primary/40" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-8" data-testid="you-get-chips">
            {YOU_GET.map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5 bg-white border border-emerald-100 rounded-full px-3.5 py-1.5 text-xs text-gray-700 font-medium">
                <Check className="w-3.5 h-3.5 text-emerald-600" /> {item}
              </span>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate("/questionnaire")}
              data-testid="howitworks-cta-btn"
              className="w-full sm:w-auto h-14 px-10 rounded-full bg-primary hover:bg-emerald-800 text-white text-base font-bold shadow-lg shadow-emerald-600/30 hover:scale-[1.02] transition-all inline-flex items-center justify-center gap-2"
            >
              Start Your Free Assessment <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <HerbOfWeek />

      {/* ============ PREMIUM VITALITY TESTS (secondary) ============ */}
      <section id="vitality-tests" className="py-14 bg-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-3.5 py-1.5 rounded-full mb-3 text-xs font-bold">
              <Crown className="w-3.5 h-3.5" /> GO DEEPER · PREMIUM
            </div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-2">Find Out What Your Body Is Telling You</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Two-minute scored self-checks for deeper insight into vitality and balance.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <button onClick={gatedPremium("/mens-scorecard")} className="group text-left" data-testid="self-check-mens-btn">
              <div className="bg-white rounded-3xl p-6 border-2 border-blue-100 hover:border-blue-400 shadow-sm hover:shadow-xl transition-all h-full">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/30">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  {!isPremium && <Lock className="w-4 h-4 text-gray-400" />}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Men's Vitality Check</h3>
                <p className="text-sm text-gray-600 mb-4">Stamina, stress, sleep quality and energy patterns.</p>
                <span className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all">
                  Start the check <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </button>
            <button onClick={gatedPremium("/womens-assessment")} className="group text-left" data-testid="self-check-womens-btn">
              <div className="bg-white rounded-3xl p-6 border-2 border-pink-100 hover:border-pink-400 shadow-sm hover:shadow-xl transition-all h-full">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-11 h-11 bg-pink-600 rounded-xl flex items-center justify-center shadow-md shadow-pink-500/30">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  {!isPremium && <Lock className="w-4 h-4 text-gray-400" />}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Women's Hormone Assessment</h3>
                <p className="text-sm text-gray-600 mb-4">Mood, energy, sleep patterns and hormonal balance.</p>
                <span className="inline-flex items-center gap-2 text-pink-600 font-semibold text-sm group-hover:gap-3 transition-all">
                  Start the assessment <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </button>
          </div>
          <p className="text-center text-sm text-gray-500 mt-5">
            {user
              ? hasSignedAgreement
                ? (isPremium
                    ? `Welcome back, ${user.first_name || user.email.split("@")[0]}. Your premium tests are unlocked.`
                    : `Welcome, ${user.first_name || user.email.split("@")[0]}. Upgrade to unlock the vitality tests — £4.99/mo.`)
                : "Sign the quick liability agreement to continue."
              : "New here? Sign up free with email and password — premium vitality tests are £4.99/mo."}
          </p>
        </div>
      </section>

      {/* ============ SOCIAL PROOF ============ */}
      <section className="py-14 bg-gradient-to-b from-emerald-50/60 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why People Choose Elite Herbalist</h2>
          <div className="flex flex-wrap justify-center gap-2.5" data-testid="why-choose-section">
            {WHY_CHOOSE.map((label) => (
              <span key={label} className="inline-flex items-center gap-2 bg-white border border-gray-100 shadow-sm rounded-full px-4 py-2.5 text-sm font-semibold text-gray-800">
                <Check className="w-4 h-4 text-primary" /> {label}
              </span>
            ))}
          </div>
          <Testimonials />
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-16 bg-primary relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4">Start Your Herbal Wellness Journey Today</h2>
          <p className="text-lg text-white/90 mb-7 max-w-2xl mx-auto">
            Personalised educational herbal recommendations in under 2 minutes — tailored to your answers, not medical advice.
          </p>
          <Button size="lg" onClick={() => navigate("/questionnaire")} data-testid="bottom-cta-btn" className="w-full sm:w-auto h-14 px-10 rounded-full bg-white text-primary hover:bg-white/90 shadow-2xl text-lg font-bold hover:scale-105 transition-transform">
            Start Your Free Assessment <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="mt-5 text-white/70 text-sm">No credit card required · 95+ herbs · Safety-focused</p>
        </div>
      </section>

      {/* ============ FOLLOW US (compact) ============ */}
      <section className="py-10 bg-[#0F3D2E]" data-testid="follow-us-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <h2 className="font-playfair text-xl md:text-2xl font-bold text-white">Follow the Journey</h2>
            <p className="text-emerald-100/70 text-sm">Daily herbal tips, remedy recipes and behind-the-scenes.</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="https://instagram.com/elite_herbalist" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="follow-instagram-btn"
               className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
              <InstagramIcon className="w-5 h-5" />
            </a>
            <a href="https://tiktok.com/@eliteherbalist" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="follow-tiktok-btn"
               className="w-12 h-12 rounded-2xl bg-black text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
              <TikTokIcon className="w-5 h-5" />
            </a>
            <a href="https://youtube.com/@EliteHerbalist" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="follow-youtube-btn"
               className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
              <YouTubeIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
            <div className="flex flex-col items-center md:items-start">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <span className="font-playfair text-xl font-bold text-primary">Elite Herbalist</span>
              </div>
              <p className="text-sm text-gray-600 text-center md:text-left">
                Your trusted source for personalized herbal wellness recommendations.
              </p>
            </div>

            <div className="text-center md:text-left">
              <h3 className="font-semibold text-gray-900 mb-4">Contact Us</h3>
              <div className="space-y-3">
                <a href="mailto:contact@eliteherbalist.com" className="flex items-center justify-center md:justify-start gap-2 text-gray-600 hover:text-primary transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  contact@eliteherbalist.com
                </a>
                <p className="text-xs text-gray-500">General inquiries</p>
              </div>
            </div>

            <div className="text-center md:text-left" data-testid="footer-explore-links">
              <h3 className="font-semibold text-gray-900 mb-4">Explore</h3>
              <div className="space-y-2 text-sm">
                <Link to="/herbs" className="block text-gray-600 hover:text-primary transition-colors">Herb Encyclopedia</Link>
                <Link to="/news" className="block text-gray-600 hover:text-primary transition-colors">Daily News</Link>
                <Link to="/store" className="block text-gray-600 hover:text-primary transition-colors">Herb Store</Link>
                <Link to="/shop" className="block text-gray-600 hover:text-primary transition-colors">Shop Infuso Teas</Link>
                <Link to="/compare/ashwagandha-vs-rhodiola" className="block text-gray-600 hover:text-primary transition-colors">Ashwagandha vs Rhodiola</Link>
                <Link to="/compare/chamomile-vs-valerian-root" className="block text-gray-600 hover:text-primary transition-colors">Chamomile vs Valerian</Link>
                <Link to="/white-label" className="block text-gray-600 hover:text-primary transition-colors">White-Label for Stores</Link>
                <Link to={isPremium ? "/womens-hormone-guide" : "/#vitality-tests"} className="block text-gray-600 hover:text-primary transition-colors">Women's Wellness</Link>
                <Link to={isPremium ? "/mens-vitality-guide" : "/#vitality-tests"} className="block text-gray-600 hover:text-primary transition-colors">Men's Wellness</Link>
              </div>
            </div>

            <div className="text-center md:text-left" data-testid="footer-practitioner-referrals">
              <h3 className="font-semibold text-gray-900 mb-4">Practitioner Referrals</h3>
              <p className="text-sm text-gray-600 mb-3">Need one-to-one human support? Speak to a qualified herbalist.</p>
              <a href="https://www.herbalalliance.org/" target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center md:justify-start gap-1.5 text-sm text-primary font-semibold hover:underline underline-offset-2"
                 data-testid="footer-herbal-alliance-link">
                Find a herbalist — Herbal Alliance <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>

            <div className="text-center md:text-left">
              <h3 className="font-semibold text-gray-900 mb-4">Herb Information</h3>
              <div className="space-y-3">
                <a href="mailto:info@eliteherbalist.com" className="flex items-center justify-center md:justify-start gap-2 text-gray-600 hover:text-primary transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  info@eliteherbalist.com
                </a>
                <p className="text-xs text-gray-500">Questions about herbs</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-6 text-xs text-gray-500" data-testid="footer-legal-links">
              <Link to="/about" className="hover:text-primary">About</Link>
              <Link to="/contact" className="hover:text-primary">Contact</Link>
              <Link to="/faq" className="hover:text-primary">FAQ</Link>
              <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-primary">Terms & Conditions</Link>
              <Link to="/disclosure" className="hover:text-primary">Affiliate & Retail Disclosure</Link>
              <Link to="/returns" className="hover:text-primary">Returns & Refunds</Link>
              <Link to="/medical-disclaimer" className="hover:text-primary">Medical Disclaimer</Link>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600">Educational wellness guidance. Not medical diagnosis or treatment.</p>
              <SocialLinks />
              <p className="text-xs text-gray-400 text-center md:text-right">
                © 2026 Elite Herbalist · Elite Herbalist LTD · Company No. 17400097<br />
                Registered office: 3 New Village Avenue, London E14 0ND, UK
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
